/**
 * The season timelapse decoder (chronicle.md §10.1, R1595).
 *
 * The byte string is the one `server/internal/timelapse` writes. The world
 * socket carries it in `TimelapseState.data`, and the public route of the
 * meta server serves it as its body, so one decoder reads both. The Go
 * package is the format's source; `timelapse.test.ts` holds this decoder
 * to the Go fixture `server/internal/timelapse/testdata/timelapse-vectors.json`.
 *
 *   byte     version (1)
 *   uvarint  cols, rows, every_sec, count
 *   count × frame: uvarint t_sec, then (uvarint length, byte code) runs
 *                  until the lengths sum to cols × rows
 *
 * A frame codes against the frame before it, and the first frame against
 * a frame of unclaimed cells (owner code 0, R1594). A run of KEEP repeats the cells of the frame
 * before; any other code sets that code.
 */

/** The format version, the first byte of every byte string. */
export const TIMELAPSE_VERSION = 1;
/** The owner code of a ridge cell, which no faction ever owns (R1594). */
export const TIMELAPSE_RIDGE = 255;
/** The run code that repeats the cells of the frame before. It is never
 *  the code of a decoded cell. */
const KEEP = 254;
/** The bounds of the Go decoder, so both refuse the same byte strings. */
const MAX_CELLS = 1 << 20;
const MAX_FRAMES = 1 << 12;

/** One owner frame: the owner code of every cell, in cell index order. A
 *  code from 1 to the faction count is a faction slot, the war banner on a
 *  Clan War world. */
export interface TimelapseFrame {
  /** World seconds since the season started. */
  tSec: number;
  cells: Uint8Array;
}

/** The decoded season timelapse of one season. */
export interface Timelapse {
  cols: number;
  rows: number;
  /** The cadence of the newest frames, in world seconds. A thinned season
   *  holds older frames at half this step or less (R1596), so a player
   *  reads `tSec` of each frame and not this figure. */
  everySec: number;
  frames: TimelapseFrame[];
}

/** decodeTimelapse reads one byte string. An empty input is a season with
 *  no frame yet, and answers null. A byte string the Go package did not
 *  write throws. */
export function decodeTimelapse(b: Uint8Array): Timelapse | null {
  if (b.length === 0) return null;
  if (b[0] !== TIMELAPSE_VERSION) throw new Error('timelapse: unknown version');
  let off = 1;
  const next = (): number => {
    let v = 0;
    let mul = 1;
    for (let i = off; i < b.length && i < off + 10; i++) {
      const x = b[i];
      v += (x & 0x7f) * mul;
      if (x < 0x80) {
        off = i + 1;
        return v;
      }
      mul *= 128;
    }
    throw new Error('timelapse: malformed byte string');
  };
  const cols = next();
  const rows = next();
  const everySec = next();
  const count = next();
  const n = cols * rows;
  if (cols === 0 || rows === 0 || n > MAX_CELLS || count > MAX_FRAMES) {
    throw new Error('timelapse: malformed byte string');
  }
  const frames: TimelapseFrame[] = [];
  let prev = new Uint8Array(n);
  for (let k = 0; k < count; k++) {
    const tSec = next();
    const cells = prev.slice();
    for (let at = 0; at < n; ) {
      const len = next();
      if (len === 0 || len > n - at || off >= b.length) {
        throw new Error('timelapse: malformed byte string');
      }
      const code = b[off++];
      if (code !== KEEP) cells.fill(code, at, at + len);
      at += len;
    }
    frames.push({ tSec, cells });
    prev = cells;
  }
  if (off !== b.length) throw new Error('timelapse: malformed byte string');
  return { cols, rows, everySec, frames };
}
